import React from 'react';
import Layout from './Layout';
import { TrendingDown, Zap, Recycle, Leaf, Upload } from 'lucide-react';
import { useData } from '../context/DataContext';

const Dashboard: React.FC = () => {
  const { inputData, results, hasCustomData, scenarioName } = useData();

  // Use uploaded data if available, otherwise use default metrics
  const metrics = hasCustomData && results ? [
    {
      title: 'CO₂ Savings',
      value: `${results.co2Savings.toFixed(1)} tCO₂e`,
      change: 'Live Data',
      trend: 'down',
      icon: TrendingDown,
      color: 'text-green-600'
    },
    {
      title: 'RDF Integration',
      value: `${inputData.rdfAvailable}%`,
      change: 'Current Level',
      trend: 'up',
      icon: Recycle,
      color: 'text-purple-600'
    },
    {
      title: 'Biogas Integration',
      value: `${inputData.biogasIntegration}%`,
      change: 'Current Level',
      trend: 'up',
      icon: Leaf,
      color: 'text-green-600'
    },
    {
      title: 'Financial Benefit',
      value: `$${(results.financialOutcomes.netBenefit / 1000).toFixed(0)}k`,
      change: 'Net Value',
      trend: 'up',
      icon: Zap,
      color: 'text-blue-600'
    }
  ] : [
    {
      title: 'CO₂ Reduction',
      value: '2,847 tCO₂e',
      change: '-12.3%',
      trend: 'down',
      icon: TrendingDown,
      color: 'text-green-600'
    },
    {
      title: 'Coal Displacement',
      value: '1,234 tons',
      change: '+8.7%',
      trend: 'up',
      icon: Zap,
      color: 'text-blue-600'
    },
    {
      title: 'Waste Processed',
      value: '4,567 tons',
      change: '+15.2%',
      trend: 'up',
      icon: Recycle,
      color: 'text-purple-600'
    },
    {
      title: 'Biogas Generated',
      value: '892 MWh',
      change: '+22.1%',
      trend: 'up',
      icon: Leaf,
      color: 'text-green-600'
    }
  ];

  const plants = [
    { name: 'Steel Plant A - Mumbai', status: 'Active', efficiency: 94, co2Reduction: 1247 },
    { name: 'Cement Plant B - Chennai', status: 'Active', efficiency: 87, co2Reduction: 892 },
    { name: 'Steel Plant C - Pune', status: 'Maintenance', efficiency: 0, co2Reduction: 0 },
    { name: 'Cement Plant D - Bangalore', status: 'Active', efficiency: 91, co2Reduction: 708 }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            {hasCustomData && (
              <div className="flex items-center mt-2 text-sm">
                <Upload className="h-4 w-4 text-blue-600 mr-2" />
                <span className="text-blue-600 font-medium">
                  Using uploaded data: {scenarioName}
                </span>
              </div>
            )}
          </div>
          <div className="text-sm text-gray-500">
            Last updated: {new Date().toLocaleString()}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div key={index} className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <Icon className={`h-8 w-8 ${metric.color}`} />
                </div>
                <div className="mt-4 flex items-center">
                  <span className={`text-sm font-medium ${
                    metric.trend === 'down' ? 'text-green-600' : 'text-blue-600'
                  }`}>
                    {metric.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-2">vs last month</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Plant Status</h2>
            <div className="space-y-4">
              {plants.map((plant, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">{plant.name}</h3>
                    <p className="text-sm text-gray-500">
                      Status: <span className={`font-medium ${
                        plant.status === 'Active' ? 'text-green-600' : 'text-yellow-600'
                      }`}>{plant.status}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{plant.efficiency}% Efficiency</p>
                    <p className="text-sm text-gray-500">{plant.co2Reduction} tCO₂e saved</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Performance Indicators</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">MRV Compliance Rate</span>
                <span className="font-semibold text-green-600">98.7%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Article 6 Readiness</span>
                <span className="font-semibold text-blue-600">TRL 5-6</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Climate Finance Potential</span>
                <span className="font-semibold text-purple-600">$2.4M</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Circular Economy Score</span>
                <span className="font-semibold text-green-600">87/100</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Alerts & Notifications</h2>
          <div className="space-y-3">
            <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-800">
                  Steel Plant A achieved 15% coal reduction milestone using biogas integration
                </p>
                <p className="text-xs text-green-600">2 hours ago</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-800">
                  MRV report for Q4 2024 ready for Gold Standard verification
                </p>
                <p className="text-xs text-blue-600">5 hours ago</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-800">
                  Steel Plant C scheduled for maintenance - temporary efficiency reduction expected
                </p>
                <p className="text-xs text-yellow-600">1 day ago</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
